// src/components/ClosingChecklistView.jsx
import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import SignatureSvg from './SignatureSvg';
import { SANDART_CLOSING_SECTIONS, SANDART_CLOSING_KEYS, isSandartRecord } from '../data/sandartChecklists';

const CLOSING_SECTIONS = [
  {
    key: 'tattoo', title: 'Tattoo Equipment', emoji: '🖊️',
    items: [
      { key: 'tattoo_gun_cleaned',     label: 'Clean the gun — disassemble and take photo of clean parts' },
      { key: 'tattoo_gun_tested',      label: 'Test with alcohol to ensure clean and working' },
      { key: 'compressor_off',         label: 'Turn off the compressor' },
      { key: 'ink_bottles_cleaned',    label: 'Clean ink bottles (unclog both holes, clean tube, take photo)' },
      { key: 'baby_powder_cleaned',    label: 'Clean baby powder tube and brush with alcohol' },
      { key: 'alcohol_refilled',       label: 'Refill both alcohol bottles' },
      { key: 'ink_box_paper_replaced', label: 'Replace paper towel in ink box; use old one to clean with alcohol' },
      { key: 'glitter_box_cleaned',    label: 'Clean glitter box (glue, brushes, replace paper towel)' },
      { key: 'paper_towels_cut',       label: 'Cut and leave 20 pieces of paper towel ready' },
    ],
  },
  {
    key: 'premades', title: 'Premades', emoji: '🎀',
    items: [
      { key: 'premades_stocked',          label: 'Min 6 premades of each color with beads (3 with charms)' },
      { key: 'premades_beads_positioned', label: 'Beads at the bottom of premade, trim only the ends' },
      { key: 'premades_knot_checked',     label: 'Sailor knot firm and in correct position (beads do not fall)' },
      { key: 'display_cleaned',           label: 'Clean display with paper towel and alcohol' },
      { key: 'premades_counted',          label: 'Count premades at checkout and register on the tablet' },
    ],
  },
  {
    key: 'cleaning', title: 'General Cleaning', emoji: '🧹',
    items: [
      { key: 'combs_washed',             label: 'Wash all combs, metal and plastic clips with soap and brush' },
      { key: 'combs_container_refilled', label: 'Clean comb container and fill with water halfway — take photo' },
      { key: 'combs_disinfected',        label: 'Wipe all combs and clips with disinfectant before storing' },
      { key: 'gel_refilled',             label: 'Refill gel to the line, clean cap, cover with paper towel' },
      { key: 'mirror_cleaned',           label: 'Clean mirror with a little alcohol and paper towel' },
      { key: 'doll_cleaned',             label: "Clean doll's face and neck — no alcohol on makeup — take photo" },
      { key: 'beaders_ready',            label: 'Leave at least 5 beaders ready' },
      { key: 'price_list_cleaned',       label: 'Clean price list with paper towel and alcohol' },
      { key: 'bead_boxes_cleaned',       label: 'Clean bead boxes with paper and alcohol' },
      { key: 'counter_cleaned',          label: 'Clean kiosk counter with disinfectant wipe' },
      { key: 'bench_cleaned',            label: 'Clean bench and chair with disinfectant wipe before storing' },
      { key: 'carpet_cleaned',           label: 'Beat the carpet; if wet, leave drying under kiosk and report' },
    ],
  },
  {
    key: 'docs', title: 'Docs & Reports', emoji: '📤',
    items: [
      { key: 'printer_off',         label: 'Turn off the printer' },
      { key: 'waiting_list_copies', label: 'Make copies (waiting list + tattoo care instructions if ≤3)' },
      { key: 'sales_counted',       label: 'Count your sales and tips from the receipts' },
      { key: 'envelope_sealed',     label: 'Seal the envelope and store in the folder' },
      { key: 'daily_digital_sent',  label: 'Send Daily Digital in the management group at clock-out' },
      { key: 'photos_sent',         label: 'Send photos: doll, printer off, comb container, inks, gun, tablet' },
      { key: 'screenshot_sent',     label: 'Send screenshot of site after clock-out (still at kiosk)' },
    ],
  },
  {
    key: 'physical', title: 'Physical Closing', emoji: '🔒',
    items: [
      { key: 'tablet_left_on',           label: 'Tablet on and charged (do NOT leave charging overnight)' },
      { key: 'umbrella_closed',          label: 'Close and tie umbrella; base not on top of the extension' },
      { key: 'extension_collected',      label: 'Collect the extension cord if necessary' },
      { key: 'floor_frames_stored',      label: 'Store floor frames carefully (avoid scratches)' },
      { key: 'materials_organized',      label: 'Organize all materials in an orderly manner' },
      { key: 'app_materials_registered', label: 'Register in app what needs restocking or write "Materials OK"' },
      { key: 'doors_locked',             label: 'Lock all doors and check the locks' },
    ],
  },
];

const ALL_CLOSING_KEYS = CLOSING_SECTIONS.flatMap(s => s.items.map(i => i.key));



const ClosingChecklistView = React.forwardRef(({ closingData }, ref) => {
  if (!closingData) return <Typography>Loading...</Typography>;

  // Hotel Sand Art usa outra lista de itens; o resto da tela e igual.
  const sandart = isSandartRecord(closingData);
  const sections = sandart ? SANDART_CLOSING_SECTIONS : CLOSING_SECTIONS;
  const allKeys = sandart ? SANDART_CLOSING_KEYS : ALL_CLOSING_KEYS;

  const { date, name, resort, notes, completed_at, signature_svg,
          validated_sales, validated_tips } = closingData;

  const base = { fontFamily: '"Arial Narrow", Arial, sans-serif', fontSize: '8.5pt', color: '#111' };

  const sectionTitle = {
    ...base, fontWeight: 'bold', fontSize: '9pt',
    backgroundColor: '#D3D3D3', padding: '2px 6px',
    marginTop: '6px', marginBottom: '2px', borderLeft: '3px solid #16a34a',
  };

  const Checkbox = ({ checked }) => (
    <Box sx={{
      width: '12px', height: '12px', border: '1.5px solid #555', borderRadius: '2px',
      backgroundColor: checked ? '#16a34a' : '#fff', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {checked && (
        <svg width="8" height="8" viewBox="0 0 10 10">
          <polyline points="1,5 4,8 9,2" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" />
        </svg>
      )}
    </Box>
  );

  const totalChecked = allKeys.filter(k => closingData[k]).length;
  const missing = sections.flatMap(s => s.items.filter(i => !closingData[i.key]).map(i => i.label));

  return (
    <Box ref={ref} sx={{
      padding: '12px', backgroundColor: 'white', width: '780px',
      fontFamily: '"Arial Narrow", Arial, sans-serif', color: 'black',
      border: '1px solid black', boxSizing: 'border-box',
    }}>
      <Typography variant="h5" sx={{
        textAlign: 'center', fontWeight: 'bold', mb: 1, fontSize: '14pt',
        fontFamily: '"Arial Black", Gadget, sans-serif', textTransform: 'uppercase',
      }}>
        {sandart ? 'SAND ART CLOSING CHECKLIST' : 'CLOSING CHECKLIST'}
      </Typography>

      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: '10pt', mb: 0.3 }}><b>DATE:</b> {date}</Typography>
        <Typography sx={{ fontSize: '10pt', mb: 0.3 }}><b>NAME:</b> {name}</Typography>
        <Typography sx={{ fontSize: '10pt', mb: 0.3 }}><b>RESORT:</b> {resort}</Typography>
        {completed_at && <Typography sx={{ fontSize: '9pt', color: '#555' }}><b>Signed at:</b> {completed_at}</Typography>}
      </Box>

      {/* Valores validados */}
      {(validated_sales != null || validated_tips != null) && (
        <Box sx={{ mb: 1, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 1, p: '4px 8px' }}>
          <Grid container spacing={2}>
            {validated_sales != null && (
              <Grid item xs={6}>
                <Typography sx={{ ...base, fontWeight: 'bold' }}>
                  Validated Sales: ${parseFloat(validated_sales).toFixed(2)}
                </Typography>
              </Grid>
            )}
            {validated_tips != null && (
              <Grid item xs={6}>
                <Typography sx={{ ...base, fontWeight: 'bold' }}>
                  Validated Tips: ${parseFloat(validated_tips).toFixed(2)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      <Box sx={{ borderTop: '1.5px solid #555', mb: 1 }} />

      {/* 2 colunas */}
      <Grid container spacing={1}>
        {sections.map((section) => {
          const checkedCount = section.items.filter(i => closingData[i.key]).length;
          return (
            <Grid item xs={6} key={section.key}>
              <Box sx={sectionTitle}>
                {section.emoji} {section.title} ({checkedCount}/{section.items.length})
              </Box>
              {section.items.map((item) => {
                const checked = !!closingData[item.key];
                return (
                  <Box key={item.key} sx={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '2px 6px', borderBottom: '1px solid #f0f0f0', minHeight: '20px',
                    backgroundColor: checked ? '#f0fdf4' : '#fff',
                  }}>
                    <Checkbox checked={checked} />
                    <Typography sx={{ ...base, color: checked ? '#166534' : '#374151', flex: 1 }}>
                      {item.label}
                    </Typography>
                    {!checked && <Typography sx={{ fontSize: '7pt', color: '#ef4444', fontWeight: 'bold' }}>✗</Typography>}
                  </Box>
                );
              })}
            </Grid>
          );
        })}
      </Grid>

      {/* Resumo */}
      <Box sx={{ mt: 1.5, borderTop: '1px solid #ccc', pt: 1 }}>
        <Typography sx={{ ...base, fontWeight: 'bold', fontSize: '9pt' }}>
          SUMMARY: {totalChecked}/{allKeys.length} items completed
          {totalChecked === allKeys.length ? ' ✅ ALL DONE' : ` — ${allKeys.length - totalChecked} item(s) not checked`}
        </Typography>
        {missing.length > 0 && (
          <Box sx={{ mt: 0.5 }}>
            <Typography sx={{ ...base, fontWeight: 'bold', color: '#b91c1c', fontSize: '8pt' }}>NOT CHECKED:</Typography>
            {missing.map((label, i) => (
              <Typography key={i} sx={{ ...base, fontSize: '8pt', color: '#b91c1c', pl: 1 }}>• {label}</Typography>
            ))}
          </Box>
        )}
      </Box>

      {notes && (
        <Box sx={{ mt: 1, borderTop: '1px solid #ccc', pt: 0.5 }}>
          <Typography sx={{ ...base, fontWeight: 'bold', fontSize: '9pt' }}>NOTES:</Typography>
          <Typography sx={{ ...base, pl: 1 }}>{notes}</Typography>
        </Box>
      )}

      {/* Assinatura */}
      <Box sx={{ mt: 1.5, borderTop: '1.5px solid #555', pt: 1 }}>
        <Typography sx={{ ...base, fontWeight: 'bold', fontSize: '9pt', mb: 0.5 }}>EMPLOYEE SIGNATURE:</Typography>
        <SignatureSvg signatureSvgJson={signature_svg} width={400} height={100} />
        <Typography sx={{ ...base, fontSize: '8pt', color: '#555', mt: 0.5 }}>
          By signing, I confirm that I completed all closing checklist items truthfully and that the kiosk was properly closed.
        </Typography>
      </Box>
    </Box>
  );
});

export default ClosingChecklistView;